import { format as fnsDateFormat, formatDistance, Locale } from "date-fns";

/**
 * Coerce an ISO string or `Date` into a `Date`. Returns `null` for
 * null/undefined/empty/invalid input so callers can short-circuit
 * display.
 */
const coerceToDate = (value?: Date | string | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDuration = (
  value?: Date | string | null,
  locale?: Locale
): string => {
  const date = coerceToDate(value);
  if (!date) return "";
  const now = new Date();
  if (date > now) return "";
  return formatDistance(date, now, locale ? { locale } : undefined);
};

export const formatDate = (
  value?: Date | string | null,
  format?: string,
  locale?: Locale
): string => {
  const date = coerceToDate(value);
  if (!date) return "Invalid timestamp provided.";
  return fnsDateFormat(date, format || "d MMM, yyyy, HH:mm", {
    locale,
  });
};

export type EmailReviewer = { email: string };

/**
 * Splits a task's email reviewers between the "Approval" row's own
 * copy-review-link button and the standalone "Review link" rows.
 *
 * A single-reviewer `EMAIL` approval already renders an "Approval" row, so
 * its copy button belongs there — that's the legacy layout, and a separate
 * row would only repeat the same address. `MULTIPLE_REVIEWERS` renders the
 * reviewers dropdown *instead* of an "Approval" row, so there every reviewer
 * needs a row of its own. Extra recipients of a single-reviewer approval
 * (rare) keep standalone rows so none is silently dropped.
 */
export const splitEmailReviewers = (
  emailReviewers: EmailReviewer[] | undefined,
  isMultipleReviewers: boolean
): {
  approvalRowReviewer?: EmailReviewer;
  standaloneReviewers: EmailReviewer[];
} => {
  const reviewers = emailReviewers ?? [];
  if (isMultipleReviewers) {
    return { standaloneReviewers: reviewers };
  }
  const [first, ...rest] = reviewers;
  return { approvalRowReviewer: first, standaloneReviewers: rest };
};
